/**
 * Video adresleri (F5B-05, F5B-18): yalnızca YouTube ve Vimeo. Kayıtta adres değil sağlayıcı + video kimliği
 * tutulur; vitrin gömme adresini kendisi kurar (YouTube çerezsiz alan adı, Vimeo `dnt=1`).
 */
export type VideoProvider = 'youtube' | 'vimeo';

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d{6,12}$/;

export function isVideoId(provider: VideoProvider, id: string): boolean {
  return provider === 'youtube' ? YT_ID.test(id) : VIMEO_ID.test(id);
}

/** Video adresini sağlayıcı ve kimliğe çevirir; tanınmayan adres null. */
export function parseVideoUrl(raw: string): { provider: VideoProvider; videoId: string } | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const host = u.hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
  const parts = u.pathname.split('/').filter(Boolean);
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const id =
      parts[0] === 'watch'
        ? u.searchParams.get('v')
        : ['embed', 'shorts', 'live', 'v'].includes(parts[0] ?? '')
          ? parts[1]
          : null;
    return id && YT_ID.test(id) ? { provider: 'youtube', videoId: id } : null;
  }
  if (host === 'youtu.be') {
    const id = parts[0];
    return id && YT_ID.test(id) ? { provider: 'youtube', videoId: id } : null;
  }
  if (host === 'vimeo.com') {
    const id = parts.find((p) => VIMEO_ID.test(p));
    return id ? { provider: 'vimeo', videoId: id } : null;
  }
  if (host === 'player.vimeo.com' && parts[0] === 'video' && VIMEO_ID.test(parts[1] ?? '')) {
    return { provider: 'vimeo', videoId: parts[1]! };
  }
  return null;
}

/** İzleme sayfası (bağlantı olarak gösterim ve panel düzenlemesi için). */
export function videoWatchUrl(v: { provider: VideoProvider; videoId: string }): string {
  return v.provider === 'youtube'
    ? `https://www.youtube.com/watch?v=${encodeURIComponent(v.videoId)}`
    : `https://vimeo.com/${encodeURIComponent(v.videoId)}`;
}

/** Gömme adresi: YouTube çerezsiz alan adı, Vimeo izlemesiz (dnt). */
export function videoEmbedUrl(v: { provider: VideoProvider; videoId: string }, autoplay = true): string {
  const a = autoplay ? '1' : '0';
  return v.provider === 'youtube'
    ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(v.videoId)}?autoplay=${a}&rel=0`
    : `https://player.vimeo.com/video/${encodeURIComponent(v.videoId)}?autoplay=${a}&dnt=1`;
}
